import { Button, TextInput } from "@ameelio/ui";
import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MeetingItemData } from "../types";

// NOTE: this component is a descendant of StepperFormWrapper,
// so it should not have its own form wrapper or submit button
export default function MeetingItemForm({
  item,
  existingNames,
  onCancel,
  onSave,
  onRemove,
}: {
  item: MeetingItemData | null;
  existingNames: string[];
  onCancel: () => void;
  onSave: (item: MeetingItemData) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const {
    control,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<{
    name: string;
    description?: string;
  }>({
    mode: "all",
    defaultValues: {
      name: item?.name ?? undefined,
      description: item?.description ?? undefined,
    },
  });

  const formData = watch();

  const isUniqueName = (value?: string) => {
    if (!value) return true;
    return !existingNames.includes(value.toLowerCase().trim());
  };

  const saveItem = async () => {
    const valid = await trigger();
    if (!valid) return;
    const normalizedFormDescription = formData.description ?? null;
    const normalizedItemDescription = item?.description ?? null;
    const isDataChanged =
      formData.name !== item?.name ||
      normalizedFormDescription !== normalizedItemDescription;
    // preserves rejected status
    if (!isDataChanged) {
      onCancel();
      return;
    }
    onSave({
      name: formData.name,
      description: formData.description,
    });
  };

  const handleCancel = () => {
    if (!item) {
      onRemove();
    } else {
      // reset form values to original title and description
      reset();
      onCancel();
    }
  };

  return (
    <Stack spacing={1} sx={{ flex: 1 }}>
      <TextInput
        control={control}
        label={t("Item")}
        name="name"
        autoFocus
        fullWidth
        rules={{
          required: t("Item name is required"),
          validate: {
            isUnique: (value) =>
              isUniqueName(value) || t("Item names must be unique"),
          },
        }}
        error={!!errors.name}
        required
      />
      <TextInput
        control={control}
        label={t("Reason")}
        name="description"
        helperText={t("Adding a reason may help staff approve an item.")}
        fullWidth
        multiline
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "space-between",
        }}
      >
        {item ? (
          <Button
            variant="text"
            onClick={onRemove}
            // ignore validation on remove
            onMouseDown={(e) => e.preventDefault()}
            color="error"
            aria-label={t("Remove")}
          >
            {t("Remove item")}
          </Button>
        ) : (
          <div /> // maintain spacing
        )}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            // ignore validation on cancel
            onMouseDown={(e) => e.preventDefault()}
            aria-label={t("Cancel")}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={saveItem}
            disabled={!!errors.name}
            aria-label={t("Save")}
          >
            {t("Save")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
